import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import TenderProcessTable from "../TenderProcessTable/TenderProcessTable";
import SubStepTracker from "../../components/SubStepTracker/SubStepTracker";
import { 
    importSourceDocuments, 
    processSpreadsheet, 
    exportSpreadsheet, 
    reselectSourceDocuments,
    cachedValidationResults
} from "../../api/api";
import { 
    ImportButton,
    Header,
    SubHeader,
    SourceDocumentsPageContainer,
    GenerateButton,
    ReselectButton,
    ButtonContainer,
    DropZoneContainer,
    DropZone,
    DropZoneLabel,
    DropZoneContent,
    FileInfo,
    DropZonesGrid
} from "../TenderProcess/TenderProcess.styles";
import Validations from './Validations'
import { useTenderFiles } from "./useTenderFiles";
import { useToast } from '../ToastProvider/ToastProvider';

const INSURERS = [
    { key: 'bryte', label: 'Bryte Life', accept: '.xls,.xlsx,.xlsm', required: true },
    { key: 'capital', label: 'Capital Alliance', accept: '.xls,.xlsx', required: true },
    { key: 'firstrand', label: 'Firstrand (FNB)', accept: '.xls,.xlsx,.xlsm', required: true },
    { key: 'hol', label: 'Hollard (Optional)', accept: '.xml,.docx', required: false, requiresBoth: true },
    { key: 'oldmutual', label: 'Old Mutual (Optional)', accept: '.xlsx', required: false }
];

const initFilesFromExisting = (existingFiles) => {
    const initial = {
        bryte: null,
        capital: null,
        firstrand: null,
        hol: { xml: null, docx: null },
        oldmutual: null
    };
    if (!existingFiles?.length) return initial;

    existingFiles.forEach(({ name, size, insurer }) => {
        const marker = { existing: true, name, size };
        if (insurer === 'hol_xml') {
            initial.hol.xml = marker;
        } else if (insurer === 'hol_docx') {
            initial.hol.docx = marker;
        } else if (insurer in initial) {
            initial[insurer] = marker;
        }
    });
    return initial;
};

const UploadSourceDocuments = ({ handleFileUpload, existingFiles }) => {
    const [files, setFiles] = useState(() => initFilesFromExisting(existingFiles));
    
    const [validationErrors, setValidationErrors] = useState({});

    const handleDrop = (insurerKey, event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const droppedFiles = Array.from(event.dataTransfer.files);
        
        if (insurerKey === 'hol') {
            handleHollardFiles(droppedFiles);
        } else {
            const file = droppedFiles[0];
            if (file && isValidFileType(file, insurerKey)) {
                setFiles(prev => ({
                    ...prev,
                    [insurerKey]: file
                }));
            }
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleFileSelect = (insurerKey, event) => {
        const selectedFiles = Array.from(event.target.files);
        
        if (insurerKey === 'hol') {
            handleHollardFiles(selectedFiles);
        } else {
            const file = selectedFiles[0];
            if (file && isValidFileType(file, insurerKey)) {
                setFiles(prev => ({
                    ...prev,
                    [insurerKey]: file
                }));
            }
        }
        event.target.value = '';
    };

    const handleHollardFiles = (fileList) => {
        const newHollardFiles = { xml: files.hol.xml, docx: files.hol.docx };
        
        fileList.forEach(file => {
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            if (ext === '.xml') {
                newHollardFiles.xml = file;
            } else if (ext === '.docx') {
                newHollardFiles.docx = file;
            }
        });
        
        setFiles(prev => ({
            ...prev,
            hol: newHollardFiles
        }));
        
        validateHollardFilesAndSetError(newHollardFiles);
    };

    const validateHollardFilesAndSetError = (hollardFiles) => {
        const hasXml = hollardFiles.xml !== null;
        const hasDocx = hollardFiles.docx !== null;
        
        if (hasXml && !hasDocx) {
            setValidationErrors(prev => ({
                ...prev,
                hol: 'Both XML and DOCX files required for Hollard'
            }));
        } else if (hasDocx && !hasXml) {
            setValidationErrors(prev => ({
                ...prev,
                hol: 'Both XML and DOCX files required for Hollard'
            }));
        } else {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.hol;
                return newErrors;
            });
        }
    };

    const isHollardValid = () => {
        const hasXml = files.hol.xml !== null;
        const hasDocx = files.hol.docx !== null;
        
        // Both present or both absent is valid
        if ((hasXml && hasDocx) || (!hasXml && !hasDocx)) {
            return true;
        }
        return false;
    };

    const isValidFileType = (file, insurerKey) => {
        const insurer = INSURERS.find(i => i.key === insurerKey);
        const acceptedTypes = insurer.accept.split(',');
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return acceptedTypes.includes(fileExtension);
    };

    const removeFile = (insurerKey, fileType = null) => {
        if (insurerKey === 'hol' && fileType) {
            const newHollardFiles = { ...files.hol, [fileType]: null };
            setFiles(prev => ({
                ...prev,
                hol: newHollardFiles
            }));
            validateHollardFilesAndSetError(newHollardFiles);
        } else {
            setFiles(prev => ({
                ...prev,
                [insurerKey]: null
            }));
        }
    };

    const requiredFilesUploaded = INSURERS
        .filter(insurer => insurer.required)
        .every(insurer => files[insurer.key] !== null) 
        && isHollardValid()
        && Object.keys(validationErrors).length === 0;

    const handleImport = () => {
        if (requiredFilesUploaded) {
            const filesToUpload = {
                bryte: files.bryte?.existing ? null : files.bryte,
                capital: files.capital?.existing ? null : files.capital,
                firstrand: files.firstrand?.existing ? null : files.firstrand,
                hol: {
                    xml: files.hol.xml?.existing ? null : files.hol.xml,
                    docx: files.hol.docx?.existing ? null : files.hol.docx,
                },
                oldmutual: files.oldmutual?.existing ? null : files.oldmutual,
            };
            const isReimport = !!existingFiles?.length;
            handleFileUpload(filesToUpload, isReimport);
        }
    };

    return (
        <DropZoneContainer>
            <DropZonesGrid>
                {INSURERS.map(insurer => {
                    if (insurer.key === 'hol') {
                        return (
                            <DropZone
                                key={insurer.key}
                                data-testid={`drop-zone-${insurer.key}`}
                                onDrop={(e) => handleDrop(insurer.key, e)}
                                onDragOver={handleDragOver}
                                hasFile={!!(files.hol.xml || files.hol.docx)}
                            >
                                <DropZoneLabel>{insurer.label}</DropZoneLabel>
                                {validationErrors.hol && (
                                    <div style={{ color: 'red', fontSize: '12px', marginBottom: '8px' }}>
                                        {validationErrors.hol}
                                    </div>
                                )}
                                {(files.hol.xml || files.hol.docx) ? (
                                    <div>
                                        {files.hol.xml && (
                                            <FileInfo>
                                                <div data-testid={`file-name-${insurer.key}-xml`}>
                                                    XML: {files.hol.xml.name}
                                                </div>
                                                <div>
                                                    {files.hol.xml.existing
                                                        ? files.hol.xml.size
                                                        : `${Math.round(files.hol.xml.size / 1024)} KB`}
                                                </div>
                                                <button 
                                                    onClick={() => removeFile(insurer.key, 'xml')}
                                                    data-testid={`remove-file-${insurer.key}-xml`}
                                                >
                                                    {files.hol.xml.existing ? 'Replace' : 'Remove'}
                                                </button>
                                            </FileInfo>
                                        )}
                                        {files.hol.docx && (
                                            <FileInfo>
                                                <div data-testid={`file-name-${insurer.key}-docx`}>
                                                    DOCX: {files.hol.docx.name}
                                                </div>
                                                <div>
                                                    {files.hol.docx.existing
                                                        ? files.hol.docx.size
                                                        : `${Math.round(files.hol.docx.size / 1024)} KB`}
                                                </div>
                                                <button 
                                                    onClick={() => removeFile(insurer.key, 'docx')}
                                                    data-testid={`remove-file-${insurer.key}-docx`}
                                                >
                                                    {files.hol.docx.existing ? 'Replace' : 'Remove'}
                                                </button>
                                            </FileInfo>
                                        )}
                                    </div>
                                ) : (
                                    <DropZoneContent>
                                        <div>Drag & drop Hollard files here (XML + DOCX)</div>
                                        <div>or</div>
                                        <label>
                                            <input
                                                type="file"
                                                accept={insurer.accept}
                                                onChange={(e) => handleFileSelect(insurer.key, e)}
                                                multiple
                                                style={{ display: 'none' }}
                                                data-testid={`file-input-${insurer.key}`}
                                            />
                                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                                click to browse
                                            </span>
                                        </label>
                                    </DropZoneContent>
                                )}
                            </DropZone>
                        );
                    }
                    
                    return (
                        <DropZone
                            key={insurer.key}
                            data-testid={`drop-zone-${insurer.key}`}
                            onDrop={(e) => handleDrop(insurer.key, e)}
                            onDragOver={handleDragOver}
                            hasFile={!!files[insurer.key]}
                        >
                            <DropZoneLabel>{insurer.label}</DropZoneLabel>
                            {files[insurer.key] ? (
                                <FileInfo>
                                    <div data-testid={`file-name-${insurer.key}`}>
                                        {files[insurer.key].name}
                                    </div>
                                    <div>
                                        {files[insurer.key].existing
                                            ? files[insurer.key].size
                                            : `${Math.round(files[insurer.key].size / 1024)} KB`}
                                    </div>
                                    <button 
                                        onClick={() => removeFile(insurer.key)}
                                        data-testid={`remove-file-${insurer.key}`}
                                    >
                                        {files[insurer.key].existing ? 'Replace' : 'Remove'}
                                    </button>
                                </FileInfo>
                            ) : (
                                <DropZoneContent>
                                    <div>Drag & drop {insurer.label} file here</div>
                                    <div>or</div>
                                    <label>
                                        <input
                                            type="file"
                                            accept={insurer.accept}
                                            onChange={(e) => handleFileSelect(insurer.key, e)}
                                            style={{ display: 'none' }}
                                            data-testid={`file-input-${insurer.key}`}
                                        />
                                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                            click to browse
                                        </span>
                                    </label>
                                </DropZoneContent>
                            )}
                        </DropZone>
                    );
                })}
            </DropZonesGrid>
            <ImportButton 
                data-testid="import-button" 
                onClick={handleImport}
                disabled={!requiredFilesUploaded}
                style={{ 
                    opacity: requiredFilesUploaded ? 1 : 0.5,
                    cursor: requiredFilesUploaded ? 'pointer' : 'not-allowed'
                }}
            >
                Import
            </ImportButton>
        </DropZoneContainer>
    );
};

const ReviewFiles = ({ handleReselectFiles, handleProcessSpreadsheet, files }) => {
    return (
        <>
            <TenderProcessTable files={files}/>
            <ButtonContainer>
                <ReselectButton data-testid="reselect-button" onClick={handleReselectFiles}>Reselect Files</ReselectButton>
                <GenerateButton data-testid="generate-button" onClick={handleProcessSpreadsheet}>Generate</GenerateButton>
            </ButtonContainer>
        </>
    );
};

const SaveSummarySpreadsheet = ({ handleExportSpreadsheet, validationData }) => {
    return (
        <>
            <Validations validationData={validationData} />
            <ImportButton data-testid="save-to-sharepoint-button" onClick={handleExportSpreadsheet}>
                Save File
            </ImportButton>
        </>
    );
};


const mapSubSteptoNumber = (subStep) => {
    if (subStep == null) return 1;
    return {
        'upload': 1,
        'process': 2,
        'generate': 3
    }[subStep]
}

const SourceDocuments = ({tenderId, subStep}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const existingFiles = location.state?.existingFiles ?? [];
    const [step, setStep] = useState(mapSubSteptoNumber(subStep));
    const [loading, setLoading] = useState(false);
    const [validationData, setValidationData] = useState([]);
    const { files, setFiles } = useTenderFiles({ tenderId, step, setLoading });

    // Fetch cached validation results if user navigates directly to step 3
    useEffect(() => {
        const fetchCachedValidations = async () => {
            if (step === 3 && validationData.length === 0) {
                try {
                    const response = await cachedValidationResults(tenderId, 'source_documents');
                    const transformedValidations = transformValidationData(response.validations || []);
                    setValidationData(transformedValidations);
                } catch (error) {
                    console.error("Error fetching cached validation results:", error);
                    // Silently fail - user can still proceed, just won't see cached validations
                }
            }
        };

        fetchCachedValidations();
    }, [step, tenderId]);

    const handleFileUpload = async (selectedFiles, isReimport = false) => {
        setLoading(true);
        try {
            const result = await importSourceDocuments(tenderId, selectedFiles, isReimport);
            const data = await result.json();
            setFiles(data.files);
            setStep(2);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file upload:", error);
            showToast('Ensure correct documents are selected.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReselectFiles = async () => {
        setLoading(true);
        try {
            await reselectSourceDocuments(tenderId);
            setFiles([]);
            setStep(1);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file reselect:", error);
            showToast('Something went wrong, please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessSpreadsheet = async () => {
        setLoading(true);
        try {
            const data = await processSpreadsheet(tenderId);
            const transformedValidations = transformValidationData(data.validations || []);
            setValidationData(transformedValidations);
            setStep(3);
            navigate(`/${tenderId}/source-documents/generate`);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet processing:", error);
            showToast('Ensure correct documents were imported.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleExportSpreadsheet = async () => {
        setLoading(true);        
        try {
            await exportSpreadsheet(tenderId);
            navigate(`/${tenderId}/finalized-summary`);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet export:", error);
            showToast('An error occurred. Please check documents.', 'error');
        } finally {
            setLoading(false);
        }
    }

    const transformValidationData = (backendValidations) => {
        const transformed = [];
        
        backendValidations.forEach(categoryData => {
            const { category, results } = categoryData;
            
            results.forEach(result => {
                transformed.push({
                    category: category,
                    field_name: result.field_name,
                    tab: result.tab,
                    row: result.line,
                    rule: result.rule,
                    passed: result.valid
                });
            });
        });
        
        return transformed;
    };

    const stepMappings = {
        1: {
            title: "Upload insurer source documents",
            subtitle: "Bryte Life, Firstrand, Hollard and Capital Alliance.",
            component: <UploadSourceDocuments handleFileUpload={handleFileUpload} existingFiles={existingFiles} />,
        },
        2: {
            title: "Review Files",
            subtitle: "Ensure the uploaded files are correct",
            component: <ReviewFiles handleReselectFiles={handleReselectFiles} handleProcessSpreadsheet={handleProcessSpreadsheet} files={files} />,
        },
        3: {
            title: "Import Complete",
            subtitle: "Your draft summary breakdown is ready",
            component: <SaveSummarySpreadsheet handleExportSpreadsheet={handleExportSpreadsheet} validationData={validationData} />,
        },
    };

    return (
        <SourceDocumentsPageContainer step={step}>
            <SubStepTracker currentStep={step} />
            <Header data-testid="tenderHeader">{stepMappings[step].title}</Header>
            <SubHeader data-testid="tenderSubheader">{stepMappings[step].subtitle}</SubHeader>
            {loading ? (
                <img src="/Icons/jigsaw-loading.gif" alt="Loading" data-testid="loading-screen" />
            ) : (
                stepMappings[step].component
            )}
        </SourceDocumentsPageContainer>
    );
};

export default SourceDocuments;
